// MIDAS V3 — UI state slice (Phase 1 scaffold)
//
// Source spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/MIDAS_V3_PHASE1_SCAFFOLD_SPEC.md §5
// Master design: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/MIDAS_V3_MASTER_DESIGN.md §5
//
// v3 introduces its own slice (`midasV3`) so that no v2 slice is mutated by v3
// work. v3 reads `chatBotSlice` (machine context) and `midasHistoryPageSlice`
// (threads) verbatim, and only adds UI state that v2 doesn't track.
//
// Phase 1 wires: theme (claude | machineid, persisted to localStorage),
// railCollapsed (persisted), and stub fields for the artifact dock (Phase 3).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens — see CLAUDE.md MIDAS-V3-P1 spawn rules.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  addOptimisticThread,
  reconcileOptimisticThread,
} from "./midasPageSlice";
// MODEL-PICKER (spec §4A point 2 / §5): the closed wire-contract union lives
// in chatBotModels.ts (pure module — shared by ChatBotApi + chatBotSlice +
// this slice without import cycles). Re-exported here because this slice is
// the state home for per-thread selections.
import {
  DEFAULT_MODEL_ID,
  MODEL_OVERRIDE_IDS,
  ModelOverrideId,
  PIPELINE_OVERRIDE_IDS,
  PIPELINE_OVERRIDE_V1,
  PIPELINE_OVERRIDE_V2,
  PipelineOverrideId,
} from "../../shared/chatBot/models/chatBotModels";

export {
  DEFAULT_MODEL_ID,
  MODEL_OVERRIDE_IDS,
  PIPELINE_OVERRIDE_IDS,
  PIPELINE_OVERRIDE_V1,
  PIPELINE_OVERRIDE_V2,
};
export type { ModelOverrideId, PipelineOverrideId };

// ============================================================
// Types
// ============================================================

export type MidasV3Theme = "claude" | "machineid";

/** Composer key for a thread that doesn't exist yet (no thread_id until
 *  `thread_created`). Migrated draft → optimistic temp id → real id via the
 *  extraReducers below. */
export const DRAFT_THREAD_KEY = "draft";

export interface MidasV3State {
  /** Active palette class — `.p-claude` (default) or `.p-machineid`. */
  theme: MidasV3Theme;
  /** Whether the history rail is collapsed (56px) vs expanded (248px). */
  railCollapsed: boolean;
  /** Width of the artifact dock when one is open (Phase 3 will read this). */
  artifactDockWidth: number;
  /** Id of the artifact currently rendered fullscreen, or null. */
  fullscreenArtifactId: string | null;
  /** Per-artifact-id overlay visibility map. Default visible = true. */
  overlayVisibility: Record<string, boolean>;
  /**
   * DIAG-OVERLAY-B — pending transient component flashes, keyed by GLOBAL
   * overlay page number.
   *
   * Armed by a `<comp-ref>` chip click for EVERY page the chip names (spec §4
   * steps 3-4). An entry is consumed by the diagram viewer the moment that
   * page's sheet is visible AND its hotspots have loaded — which makes the
   * visible-sheet case (rule (a)) and the reveal-later case (rule (e)) ONE
   * mechanism instead of two. Entries for pages the user never opens simply
   * sit here until the next chip click replaces them; no timed expiry in v1
   * (spec §4.7).
   */
  pendingCompFlash: Record<number, { component: string; nonce: number }>;
  /**
   * Monotonic arm counter. Carried into each pending entry so the viewer can
   * distinguish "the same component armed AGAIN" from "the same prop value
   * re-rendered" — without it, clicking the same chip twice would not re-flash.
   */
  compFlashNonce: number;
  /**
   * DIAG-OVERLAY-B — transient tab-pill blink target (spec rules (b)/(c)):
   * the chip pages that are NOT the visible sheet. `armedAt` lets
   * ArtifactHeaderV3 discard a blink that was armed while the dock was closed
   * (nobody was mounted to clear it) instead of blinking stale tabs on open.
   */
  compFlashBlink: { pages: number[]; armedAt: number } | null;
  /** MODEL-PICKER: per-thread model selection. Absent key = default
   *  (Sonnet 4.6 → NO model_override field on the wire). Client-side only —
   *  the bot has no thread stickiness (spec Q(c)); every send resolves the
   *  thread's model AT DISPATCH TIME (chatBotSlice.sendMessage). */
  modelOverrideByThread: Record<string, ModelOverrideId>;
  /** Insertion order of modelOverrideByThread keys — oldest first. Used for
   *  the ~200-entry localStorage eviction cap. */
  modelOverrideOrder: string[];
  /** MODEL-PICKER-PIPELINE: per-thread answer-pipeline selection. Absent key
   *  = DEFAULT (bot-side v3, agentic diagnosis) → NO `pipeline_override`
   *  field on the wire (never ""). Present key (legal opt-out values
   *  "v1" | "v2") → the sendMessage thunk stamps `pipeline_override: "<id>"`
   *  on EVERY outgoing turn of that thread — exactly the same client-side
   *  rules as modelOverrideByThread (per-message, no server stickiness). */
  pipelineOverrideByThread: Record<string, PipelineOverrideId>;
  /** Insertion order of pipelineOverrideByThread keys — oldest first. Same
   *  eviction cap as modelOverrideOrder. */
  pipelineOverrideOrder: string[];
}

// ============================================================
// localStorage hydration
// ============================================================

const LS_THEME_KEY = "midas-v3-theme";
const LS_RAIL_COLLAPSED_KEY = "midas-v3-rail-collapsed";

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

const readTheme = (): MidasV3Theme => {
  const ls = safeLocalStorage();
  if (!ls) return "claude";
  const raw = ls.getItem(LS_THEME_KEY);
  if (raw === "claude" || raw === "machineid") return raw;
  return "claude";
};

const writeTheme = (theme: MidasV3Theme): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_THEME_KEY, theme);
  } catch {
    // localStorage may throw in private mode / quota; not fatal for v3 UX.
  }
};

const readRailCollapsed = (): boolean => {
  const ls = safeLocalStorage();
  if (!ls) return false;
  const raw = ls.getItem(LS_RAIL_COLLAPSED_KEY);
  return raw === "1";
};

const writeRailCollapsed = (collapsed: boolean): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(LS_RAIL_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // ignore
  }
};

// ── MODEL-PICKER persistence (spec §5) ────────────────────────
// Stored as an ordered array of [threadKey, modelId] pairs so a reload
// doesn't silently flip a thread back to the default model. Capped at ~200
// entries — oldest inserted evicted first. Per-browser key (not per-user):
// the slice hydrates at module init, before any auth state exists, and the
// selection is a harmless UI preference (the server allowlist is the real
// boundary), so per-browser scope is acceptable (design open-Q 3 pattern).
const LS_MODEL_BY_THREAD_KEY = "midas.v3.modelByThread";
const MODEL_MAP_CAP = 200;

// v1.1 (§14.1): derived from the closed union constant so the hydration
// guard can never drift from the allowlist (was 3 hardcoded literals).
const isModelOverrideId = (v: unknown): v is ModelOverrideId =>
  typeof v === "string" &&
  (MODEL_OVERRIDE_IDS as readonly string[]).includes(v);

const readModelMap = (): {
  map: Record<string, ModelOverrideId>;
  order: string[];
} => {
  const empty = { map: {} as Record<string, ModelOverrideId>, order: [] as string[] };
  const ls = safeLocalStorage();
  if (!ls) return empty;
  try {
    const raw = ls.getItem(LS_MODEL_BY_THREAD_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return empty;
    const map: Record<string, ModelOverrideId> = {};
    const order: string[] = [];
    for (const entry of parsed) {
      if (
        Array.isArray(entry) &&
        typeof entry[0] === "string" &&
        isModelOverrideId(entry[1]) &&
        entry[1] !== DEFAULT_MODEL_ID &&
        !(entry[0] in map)
      ) {
        map[entry[0]] = entry[1];
        order.push(entry[0]);
      }
    }
    return { map, order };
  } catch {
    return empty;
  }
};

const writeModelMap = (
  map: Record<string, ModelOverrideId>,
  order: string[]
): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    const pairs = order
      .filter((k) => k in map)
      .map((k) => [k, map[k]] as [string, ModelOverrideId]);
    ls.setItem(LS_MODEL_BY_THREAD_KEY, JSON.stringify(pairs));
  } catch {
    // localStorage may throw in private mode / quota; not fatal.
  }
};

/** Shared mutation helper: set/remove a thread's model + maintain order +
 *  eviction cap + persist. Default model REMOVES the key (absent = default,
 *  no field on the wire). */
const applyThreadModel = (
  state: MidasV3State,
  threadKey: string,
  model: ModelOverrideId
): void => {
  if (model === DEFAULT_MODEL_ID) {
    if (threadKey in state.modelOverrideByThread) {
      delete state.modelOverrideByThread[threadKey];
      state.modelOverrideOrder = state.modelOverrideOrder.filter(
        (k) => k !== threadKey
      );
    }
  } else {
    if (!(threadKey in state.modelOverrideByThread)) {
      state.modelOverrideOrder.push(threadKey);
      // Eviction cap — drop oldest entries beyond the cap.
      while (state.modelOverrideOrder.length > MODEL_MAP_CAP) {
        const evicted = state.modelOverrideOrder.shift();
        if (evicted) delete state.modelOverrideByThread[evicted];
      }
    }
    state.modelOverrideByThread[threadKey] = model;
  }
  writeModelMap(state.modelOverrideByThread, state.modelOverrideOrder);
};

/** Re-key a thread's model selection (draft → temp id → real id). No-op when
 *  the old key has no selection. */
const migrateThreadModelKey = (
  state: MidasV3State,
  oldKey: string,
  newKey: string
): void => {
  if (oldKey === newKey) return;
  const model = state.modelOverrideByThread[oldKey];
  if (!model) return;
  delete state.modelOverrideByThread[oldKey];
  state.modelOverrideOrder = state.modelOverrideOrder.map((k) =>
    k === oldKey ? newKey : k
  );
  state.modelOverrideByThread[newKey] = model;
  writeModelMap(state.modelOverrideByThread, state.modelOverrideOrder);
};

// ── MODEL-PICKER-PIPELINE persistence ─────────────────────────
// Deliberately mirrors the model-map pattern above 1:1 (same pair format,
// same cap, same eviction, same draft-key migration) so the selection persists
// and resets IDENTICALLY to the model choice — no extra persistence
// invented. Absent key = DEFAULT (bot-side v3); stored values are the legal
// opt-out ids ("v1" | "v2"). A STALE persisted "v3" (written by the pre-flip
// live build, when v3 was an explicit option) fails the PIPELINE_OVERRIDE_IDS
// guard in readPipelineMap below → dropped at hydration = treated as
// no-selection/default, and purged from localStorage on the next write.
const LS_PIPELINE_BY_THREAD_KEY = "midas.v3.pipelineByThread";

const readPipelineMap = (): {
  map: Record<string, PipelineOverrideId>;
  order: string[];
} => {
  const empty = {
    map: {} as Record<string, PipelineOverrideId>,
    order: [] as string[],
  };
  const ls = safeLocalStorage();
  if (!ls) return empty;
  try {
    const raw = ls.getItem(LS_PIPELINE_BY_THREAD_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return empty;
    const map: Record<string, PipelineOverrideId> = {};
    const order: string[] = [];
    for (const entry of parsed) {
      if (
        Array.isArray(entry) &&
        typeof entry[0] === "string" &&
        typeof entry[1] === "string" &&
        (PIPELINE_OVERRIDE_IDS as readonly string[]).includes(entry[1]) &&
        !(entry[0] in map)
      ) {
        map[entry[0]] = entry[1] as PipelineOverrideId;
        order.push(entry[0]);
      }
    }
    return { map, order };
  } catch {
    return empty;
  }
};

const writePipelineMap = (
  map: Record<string, PipelineOverrideId>,
  order: string[]
): void => {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    const pairs = order
      .filter((k) => k in map)
      .map((k) => [k, map[k]] as [string, PipelineOverrideId]);
    ls.setItem(LS_PIPELINE_BY_THREAD_KEY, JSON.stringify(pairs));
  } catch {
    // localStorage may throw in private mode / quota; not fatal.
  }
};

/** Set/remove a thread's pipeline selection + maintain order + eviction cap +
 *  persist. `null` REMOVES the key (default = no field on the wire → bot-side
 *  v3) — mirrors applyThreadModel's default-model removal. A non-null id
 *  ("v1" | "v2") overwrites any prior selection (mutually exclusive). */
const applyThreadPipeline = (
  state: MidasV3State,
  threadKey: string,
  pipeline: PipelineOverrideId | null
): void => {
  if (!pipeline) {
    if (threadKey in state.pipelineOverrideByThread) {
      delete state.pipelineOverrideByThread[threadKey];
      state.pipelineOverrideOrder = state.pipelineOverrideOrder.filter(
        (k) => k !== threadKey
      );
    }
  } else {
    if (!(threadKey in state.pipelineOverrideByThread)) {
      state.pipelineOverrideOrder.push(threadKey);
      while (state.pipelineOverrideOrder.length > MODEL_MAP_CAP) {
        const evicted = state.pipelineOverrideOrder.shift();
        if (evicted) delete state.pipelineOverrideByThread[evicted];
      }
    }
    state.pipelineOverrideByThread[threadKey] = pipeline;
  }
  writePipelineMap(state.pipelineOverrideByThread, state.pipelineOverrideOrder);
};

/** Re-key a thread's pipeline toggle (draft → temp id → real id). No-op when
 *  the old key has no toggle. */
const migrateThreadPipelineKey = (
  state: MidasV3State,
  oldKey: string,
  newKey: string
): void => {
  if (oldKey === newKey) return;
  const pipeline = state.pipelineOverrideByThread[oldKey];
  if (!pipeline) return;
  delete state.pipelineOverrideByThread[oldKey];
  state.pipelineOverrideOrder = state.pipelineOverrideOrder.map((k) =>
    k === oldKey ? newKey : k
  );
  state.pipelineOverrideByThread[newKey] = pipeline;
  writePipelineMap(state.pipelineOverrideByThread, state.pipelineOverrideOrder);
};

// ============================================================
// Initial state
// ============================================================

const hydratedModelMap = readModelMap();
const hydratedPipelineMap = readPipelineMap();

const initialState: MidasV3State = {
  theme: readTheme(),
  railCollapsed: readRailCollapsed(),
  artifactDockWidth: 620,
  fullscreenArtifactId: null,
  overlayVisibility: {},
  // DIAG-OVERLAY-B — session-scoped, deliberately NOT persisted: a flash is a
  // 1.6s response to a click, meaningless after a reload.
  pendingCompFlash: {},
  compFlashNonce: 0,
  compFlashBlink: null,
  modelOverrideByThread: hydratedModelMap.map,
  modelOverrideOrder: hydratedModelMap.order,
  pipelineOverrideByThread: hydratedPipelineMap.map,
  pipelineOverrideOrder: hydratedPipelineMap.order,
};

// ============================================================
// Slice
// ============================================================

const midasV3Slice = createSlice({
  name: "midasV3",
  initialState,
  reducers: {
    setMidasV3Theme: (state, action: PayloadAction<MidasV3Theme>) => {
      state.theme = action.payload;
      writeTheme(action.payload);
    },
    toggleRailCollapsed: (state) => {
      state.railCollapsed = !state.railCollapsed;
      writeRailCollapsed(state.railCollapsed);
    },
    setRailCollapsed: (state, action: PayloadAction<boolean>) => {
      state.railCollapsed = action.payload;
      writeRailCollapsed(action.payload);
    },
    // ── Artifact dock stubs (Phase 3 wires these — included now so the
    //    slice shape is stable across phases and selectors don't need to
    //    branch on field presence). ──
    setArtifactDockWidth: (state, action: PayloadAction<number>) => {
      state.artifactDockWidth = action.payload;
    },
    setFullscreenArtifactId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.fullscreenArtifactId = action.payload;
    },
    setOverlayVisibility: (
      state,
      action: PayloadAction<{ artifactId: string; visible: boolean }>
    ) => {
      state.overlayVisibility[action.payload.artifactId] =
        action.payload.visible;
    },
    // ── DIAG-OVERLAY-B: comp-ref chip flash / blink ──
    /**
     * Arm a component flash for every page in `pages`, and a tab blink for
     * `blinkPages` (the subset that is not the visible sheet).
     *
     * LATEST CLICK WINS (spec §4.7): the pending map is REPLACED, not merged,
     * so a second chip click cannot leave a stale component armed on a tab the
     * user opens ten minutes later. The nonce bump guarantees a re-arm of the
     * same (page, component) pair is observable by the viewer.
     */
    armCompFlash: (
      state,
      action: PayloadAction<{
        component: string;
        pages: number[];
        blinkPages: number[];
      }>
    ) => {
      const { component, pages, blinkPages } = action.payload;
      if (!component || pages.length === 0) return;
      state.compFlashNonce += 1;
      const nonce = state.compFlashNonce;
      const next: Record<number, { component: string; nonce: number }> = {};
      pages.forEach((p) => {
        next[p] = { component, nonce };
      });
      state.pendingCompFlash = next;
      state.compFlashBlink =
        blinkPages.length > 0
          ? { pages: blinkPages, armedAt: Date.now() }
          : null;
    },
    /** Consume one page's pending flash — dispatched by the dock the moment the
     *  viewer has actually started the flash on that sheet. A second visit to
     *  the same tab therefore does NOT re-flash (spec acceptance §8.7). */
    consumeCompFlash: (state, action: PayloadAction<{ page: number }>) => {
      delete state.pendingCompFlash[action.payload.page];
    },
    /** Clear the transient tab blink (timer owned by ArtifactHeaderV3). */
    clearCompFlashBlink: (state) => {
      state.compFlashBlink = null;
    },
    // ── MODEL-PICKER (spec §5) ──
    /** Set the model for a thread (or the DRAFT_THREAD_KEY for a not-yet-
     *  created thread). Picking the default REMOVES the key. Persisted to
     *  localStorage on every change. */
    setThreadModel: (
      state,
      action: PayloadAction<{ threadKey: string; model: ModelOverrideId }>
    ) => {
      applyThreadModel(state, action.payload.threadKey, action.payload.model);
    },
    // ── MODEL-PICKER-PIPELINE ──
    /** Select the answer-pipeline opt-out ("v1" | "v2") for a thread (or
     *  DRAFT_THREAD_KEY for a not-yet-created thread). `null` REMOVES the key
     *  (default = no `pipeline_override` on the wire — never "" — the bot
     *  runs its v3 default). Selecting an id overwrites any prior selection
     *  (mutually exclusive). Persisted to localStorage on every change —
     *  same lifecycle as setThreadModel. */
    setThreadPipeline: (
      state,
      action: PayloadAction<{
        threadKey: string;
        pipeline: PipelineOverrideId | null;
      }>
    ) => {
      applyThreadPipeline(
        state,
        action.payload.threadKey,
        action.payload.pipeline
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // MODEL-PICKER draft-key migration (spec §5): the composer keys on
      // `activeThreadId ?? "draft"`. The optimistic-thread flow re-keys twice:
      //   draft → optimistic temp id  (addOptimisticThread, send time)
      //   temp id → real thread id    (reconcileOptimisticThread, thread_created)
      // Every send resolves the model AT DISPATCH TIME (before
      // addOptimisticThread fires), so mid-stream re-keying can't affect an
      // in-flight turn.
      // MODEL-PICKER-PIPELINE-V2: the pipeline toggle re-keys through the
      // SAME draft → temp → real migration as the model selection.
      .addCase(addOptimisticThread, (state, action) => {
        migrateThreadModelKey(state, DRAFT_THREAD_KEY, action.payload.tempId);
        migrateThreadPipelineKey(
          state,
          DRAFT_THREAD_KEY,
          action.payload.tempId
        );
      })
      .addCase(reconcileOptimisticThread, (state, action) => {
        migrateThreadModelKey(
          state,
          action.payload.tempId,
          action.payload.realId
        );
        migrateThreadPipelineKey(
          state,
          action.payload.tempId,
          action.payload.realId
        );
      });
  },
});

export const {
  setMidasV3Theme,
  toggleRailCollapsed,
  setRailCollapsed,
  setArtifactDockWidth,
  setFullscreenArtifactId,
  setOverlayVisibility,
  armCompFlash,
  consumeCompFlash,
  clearCompFlashBlink,
  setThreadModel,
  setThreadPipeline,
} = midasV3Slice.actions;

/** MODEL-PICKER: resolve the model selection for a thread key. Returns
 *  undefined for default/absent — undefined = send NO `model_override`. */
export const selectThreadModel = (
  state: { midasV3: MidasV3State },
  threadKey: string
): ModelOverrideId | undefined =>
  state.midasV3.modelOverrideByThread[threadKey];

/** MODEL-PICKER-PIPELINE: resolve the pipeline opt-out for a thread key.
 *  Returns undefined for default/absent — undefined = send NO
 *  `pipeline_override` (the bot runs its v3 default). */
export const selectThreadPipeline = (
  state: { midasV3: MidasV3State },
  threadKey: string
): PipelineOverrideId | undefined =>
  state.midasV3.pipelineOverrideByThread[threadKey];

export default midasV3Slice.reducer;
